import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { TagPreview, getEngraveArea } from "../lib/tagShapes";
import { BuilderSteps } from "../components/workshop/BuilderSteps";
import { OrderCart } from "../components/workshop/OrderCart";
import { CheckoutForm } from "../components/workshop/CheckoutForm";
import { BundlePanel } from "../components/workshop/BundlePanel";
import { GiftVoucherPanel } from "../components/workshop/GiftVoucherPanel";
import { Sparkle } from "../components/Decor2";
import { WavyBreak } from "../components/Wavy";
import { useSection } from "../context/SiteContext";
import { Pill } from "./Home";

const centerIcon = (shapeKey, size) => {
  const a = getEngraveArea(shapeKey, size);
  return a ? { x: +(a.x + a.w / 2).toFixed(1), y: +(a.y + a.h / 2).toFixed(1) } : { x: 50, y: 40 };
};

const Workshop = () => {
  const { data: page } = useSection("workshop_page");
  const [config, setConfig] = useState(null);
  const [shapeId, setShapeId] = useState(null);
  const [colourId, setColourId] = useState(null);
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [icon, setIcon] = useState(null);
  const [qty, setQty] = useState(1);
  const [stakeSize, setStakeSize] = useState("20cm");
  const [cart, setCart] = useState([]);
  const [shipMethodId, setShipMethodId] = useState("");
  const [voucher, setVoucher] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(null);

  useEffect(() => {
    api.get("/workshop/config").then((r) => {
      setConfig(r.data);
      setShapeId(r.data.shapes[0]?.id || null);
      setColourId(r.data.colours[0]?.id || null);
      const methods = (r.data.settings?.shipping_methods || []).filter((m) => m.active);
      setShipMethodId(methods[0]?.id || "");
    }).catch(() => toast.error("Could not load workshop options"));
  }, []);

  const shape = useMemo(() => config?.shapes.find((s) => s.id === shapeId), [config, shapeId]);
  const colour = useMemo(() => config?.colours.find((c) => c.id === colourId), [config, colourId]);
  const basePrice = Number(config?.settings?.base_price || 0);
  const stakePrices = config?.settings?.stake_prices || {};
  const unitPrice = shape?.key === "etchery_plant_stake" && stakePrices[stakeSize]
    ? Number(stakePrices[stakeSize])
    : Number(shape?.price || basePrice);
  const activeSize = shape?.key === "etchery_plant_stake" ? stakeSize : "";

  const selectShape = (s) => {
    setShapeId(s.id);
    setIcon((ic) => (ic ? { ...ic, ...centerIcon(s.key, s.key === "etchery_plant_stake" ? stakeSize : "") } : null));
  };
  const selectSize = (size) => {
    setStakeSize(size);
    setIcon((ic) => (ic ? { ...ic, ...centerIcon("etchery_plant_stake", size) } : null));
  };
  const pickIcon = (key) => {
    if (!key) { setIcon(null); return; }
    setIcon({ key, size: 16, rotation: 0, ...centerIcon(shape?.key, activeSize) });
  };

  const addToOrder = () => {
    if (!shape || !colour) return;
    if (!line1.trim()) { toast.error("Add at least one line of text for your tag"); return; }
    setCart((c) => [...c, {
      shape_id: shape.id, shape_key: shape.key, shape_name: shape.name,
      colour_id: colour.id, colour_name: colour.name, colour_hex: colour.hex,
      text_line1: line1.trim(), text_line2: line2.trim(),
      size: activeSize,
      icon: icon ? { ...icon } : null,
      unit_price: unitPrice,
      quantity: qty,
      uid: crypto.randomUUID(),
    }]);
    setLine1(""); setLine2(""); setQty(1);
    toast.success("Tag added to your order");
  };

  const applyVoucher = async (code) => {
    const cartSubtotal = cart.reduce((s, i) => s + i.quantity * (i.unit_price ?? basePrice), 0);
    try {
      const { data } = await api.post("/vouchers/check", { code, subtotal: cartSubtotal });
      setVoucher(data);
      toast.success(`Voucher ${data.code} applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "That voucher code isn't valid");
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (cart.length === 0) { toast.error("Add at least one tag to your order first"); return; }
    setSubmitting(true);
    try {
      const shipMethods = (config?.settings?.shipping_methods || []).filter((m) => m.active);
      const shipMethod = shipMethods.find((m) => m.id === shipMethodId) || shipMethods[0];
      const { data } = await api.post("/orders", {
        customer: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        items: cart.map(({ shape_key, shape_name, colour_name, colour_hex, bundle_name, uid, unit_price, ...rest }) => rest),
        notes: form.notes,
        shipping_method_id: shipMethod?.id || "",
        voucher_code: voucher?.code || "",
        payment_method: paymentMethod,
      });
      if (paymentMethod === "card" && data.total > 0) {
        const co = await api.post("/payments/order-checkout", { order_id: data.id, origin_url: window.location.origin });
        window.location.href = co.data.checkout_url;
        return;
      }
      setPlaced(data);
      setCart([]);
      setVoucher(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (placed) {
    return (
      <div className="bg-cream min-h-[60vh] flex items-center justify-center px-6 py-24" data-testid="order-success">
        <div className="text-center max-w-xl">
          <CheckCircle2 className="w-16 h-16 text-forest mx-auto" />
          <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl mt-6">ORDER RECEIVED!</h1>
          <p className="mt-4 text-plum-deep/80 text-lg">
            Thank you — your order number is <span className="font-semibold text-magenta" data-testid="order-number">{placed.order_number}</span>.
            We'll email you shortly to confirm details and payment.
          </p>
          <p className="mt-2 text-plum-deep/60">Order total: ${placed.total.toFixed(2)} NZD</p>
          {paymentMethod === "bank_transfer" && placed.total > 0 && (
            <div className="mt-6 bg-warmwhite border border-plum-deep/10 rounded-2xl p-5 text-left" data-testid="bank-transfer-details">
              <p className="font-serif-display uppercase tracking-[0.18em] text-sm text-plum-deep font-semibold">PAY BY BANK TRANSFER</p>
              <p className="mt-2 text-sm text-plum-deep/80">{config?.settings?.bank_account_name}</p>
              <p className="font-mono tracking-[0.1em] text-lg text-plum-deep font-bold">{config?.settings?.bank_account_number}</p>
              <p className="mt-1 text-xs text-plum-deep/60">Reference: <b>{placed.order_number}</b> · Amount: <b>${placed.total.toFixed(2)} NZD</b></p>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Pill to="/track" testid="order-success-track">TRACK YOUR ORDER</Pill>
            <Pill to="/" testid="order-success-home">BACK TO HOME</Pill>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-cream">
      <section className="relative pt-10 md:pt-16 pb-10 px-6 md:px-10 text-center overflow-hidden">
        <Sparkle className="absolute top-16 left-14 w-6 h-6" color="#EC2675" />
        <Sparkle className="absolute top-24 right-20 w-5 h-5" color="#FFD23F" />
        <span className="font-serif-display uppercase tracking-[0.3em] text-magenta text-[11px] md:text-sm font-semibold">CUSTOM PLANT TAGS, MADE TO ORDER</span>
        <h1 className="font-serif-display uppercase text-plum-deep text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[0.95]" data-testid="workshop-title">
          {page.title || "FOUNDRY WORKSHOP"}
        </h1>
        <p className="mt-5 text-plum-deep/80 text-base md:text-lg max-w-2xl mx-auto">{page.intro}</p>
        {config?.settings?.product_text && (
          <p className="mt-3 text-plum-deep/60 text-sm max-w-xl mx-auto">{config.settings.product_text}</p>
        )}
      </section>

      <WavyBreak color="#EC2675" />

      {!config ? (
        <div className="py-24 text-center text-plum-deep/60">Loading workshop…</div>
      ) : (
        <section className="px-6 md:px-10 pb-20 max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-7 space-y-10">
              <BuilderSteps
                shapes={config.shapes}
                colours={config.colours}
                shape={shape} shapeId={shapeId} colour={colour} colourId={colourId}
                stakeSize={stakeSize} basePrice={basePrice} stakePrices={stakePrices}
                line1={line1} line2={line2} icon={icon} qty={qty}
                maxLen={Number(config.settings?.max_text_length || 16)}
                maxQty={Number(config.settings?.max_quantity || 100)}
                unitPrice={unitPrice}
                onSelectShape={selectShape}
                onSelectSize={selectSize}
                onSelectColour={setColourId}
                onLine1={setLine1} onLine2={setLine2}
                onPickIcon={pickIcon}
                onIconChange={setIcon}
                onResetIcon={() => setIcon({ ...icon, rotation: 0, size: 16, ...centerIcon(shape?.key, activeSize) })}
                onQty={setQty}
                onAdd={addToOrder}
              />
              <BundlePanel
                bundles={config.bundles || []}
                shapes={config.shapes}
                colours={config.colours}
                onAdd={(item) => setCart((c) => [...c, item])}
              />
              <GiftVoucherPanel />
            </div>

            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 space-y-8">
                <div className="relative bg-warmwhite rounded-[2rem] border border-plum-deep/10 p-8 text-center" data-testid="tag-preview-panel">
                  <span className="font-serif-display uppercase tracking-[0.28em] text-magenta text-xs font-semibold">LIVE PREVIEW</span>
                  <div className="mt-4 mx-auto w-full max-w-[240px]">
                    <TagPreview
                      shapeKey={shape?.key || "etchery_panel_stake"}
                      hex={colour?.hex || "#EC2675"}
                      line1={line1 || "YOUR TEXT"}
                      line2={line2}
                      size={stakeSize}
                      icon={icon}
                      onIconChange={icon ? setIcon : null}
                      showSafeArea={Boolean(icon)}
                      className="w-full drop-shadow-[0_20px_30px_rgba(74,27,77,0.25)]"
                      testid="tag-live-preview"
                    />
                  </div>
                  <p className="mt-3 font-serif-display uppercase text-sm tracking-[0.12em] text-plum-deep/70">
                    {shape?.name}{shape?.key === "etchery_plant_stake" ? ` ${stakeSize}` : ""} · {colour?.name}
                  </p>
                </div>

                <OrderCart
                  cart={cart}
                  settings={config.settings}
                  shipMethodId={shipMethodId}
                  onSelectShipping={setShipMethodId}
                  onRemove={(i) => setCart((c) => c.filter((_, j) => j !== i))}
                  voucher={voucher}
                  onApplyVoucher={applyVoucher}
                  onRemoveVoucher={() => setVoucher(null)}
                />

                <CheckoutForm
                  form={form} setForm={setForm} submitting={submitting} disabled={cart.length === 0} onSubmit={placeOrder}
                  paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod}
                  bankName={config.settings?.bank_account_name} bankNumber={config.settings?.bank_account_number}
                />
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Workshop;
